import { ReactNode, useEffect } from 'react';

import { SizedRelativeSpinner } from '@components/loading-spinner';
import { StandaloneMenuItem } from '@components/menu';
import { SearchBox } from '@components/search-box';
import { useScopedTranslate } from '@locale/translate';

import { OptionEditorBody } from './editable-cell-popover';
import { editableCellLocale } from './locale';
import { OptionSearch } from './use-option-search';

import { EditOption } from '../types';

export type EditableOptionMenuListProps = {
  options: Array<EditOption>;
  isLoading: boolean;
  hasError?: boolean;
  load(): void;
  // Owned by the editor rather than this list, so it can be reset as the
  // popover opens.
  search: OptionSearch;
  alwaysShowSearch?: boolean;
  children(filteredOptions: Array<EditOption>): ReactNode;
};

export const EditableOptionMenuList = ({
  options,
  isLoading,
  hasError = false,
  load,
  search,
  alwaysShowSearch = false,
  children,
}: EditableOptionMenuListProps) => {
  const { t } = useScopedTranslate(editableCellLocale);
  const { filteredOptions, isSearchVisible, onSearchChange } = search;

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <OptionEditorBody>
        <StandaloneMenuItem
          label={t('loadingOptions')}
          iconRight={<SizedRelativeSpinner size={16} $skipMargin $borderWidth={2} />}
          isDisabled
        />
      </OptionEditorBody>
    );
  }

  if (hasError) {
    return (
      <OptionEditorBody>
        <StandaloneMenuItem label={t('loadOptionsError')} onSelect={load} />
      </OptionEditorBody>
    );
  }

  if (options.length === 0) {
    return (
      <OptionEditorBody>
        <StandaloneMenuItem label={t('noResults')} isDisabled />
      </OptionEditorBody>
    );
  }

  return (
    <OptionEditorBody>
      {(alwaysShowSearch || isSearchVisible) && (
        <SearchBox
          placeholder={t('searchPlaceholder')}
          value={search.search}
          onChange={onSearchChange}
          shouldBlurOnEscape={false}
        />
      )}
      {filteredOptions.length === 0 ? (
        <StandaloneMenuItem label={t('noResults')} isDisabled />
      ) : (
        children(filteredOptions)
      )}
    </OptionEditorBody>
  );
};
